import { checkPermissions } from "@elit-hair/elit-tools";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { MutableRefObject, ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { PERMISSIONS } from "@/constants/permissions";
import Panel from "@/layout/Panel";
import PanelTitle from "@/layout/PanelTitle";
import useAuth from "@/providers/hooks/useAuth";
import { usePlanChanges } from "@/stores/use-plan-table";

import PlanChangesButton from "./table/PlanChangesButton";
import PlanPanelCreateButton from "./table/PlanPanelCreateButton";

function PlanPanel({ children, apiRef }: { children: ReactNode; apiRef: MutableRefObject<GridApiPremium> }) {
    const { t } = useTranslation();

    const changes = usePlanChanges();
    const changedKeys = useMemo(() => Object.keys(changes), [changes]);
    const totals = useMemo(
        () =>
            changedKeys.reduce(
                (prev, curr) => {
                    prev.row += 1;
                    const keys = Object.keys(changes[curr]);
                    prev.cell += keys.length;
                    return prev;
                },
                {
                    row: 0,
                    cell: 0,
                }
            ),
        [changes, changedKeys]
    );
    const userPermissions = useAuth()?.userData?.permissionCodes || [];
    const canUpdate = checkPermissions(userPermissions, [PERMISSIONS.TR_SVC_MANAGE_DRIVERS]);

    return (
        <Panel
            title={
                <PanelTitle title={t("patients-plan")}>
                    {canUpdate && (
                        <div className="col-span-12 md:col-span-6 flex items-center justify-end gap-2">
                            <PlanChangesButton totals={totals} apiRef={apiRef} />
                            <PlanPanelCreateButton totals={totals} />
                        </div>
                    )}
                </PanelTitle>
            }
        >
            {children}
        </Panel>
    );
}

export default PlanPanel;
